import {FormArray, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import { ApplicationFormFieldVisibilityResolver } from './application-form-field-visibility-resolver';
import { ApplicationFormSettings } from './race-registration-form-settings';
import { RaceSettings } from './race-settings';
import { FieldConfig, FieldConfigType } from './field-config';
import { RaceAdditionalPricingItem } from './race-additional-pricing-item';
import { Competitor } from './competitor';
import { CustomFieldValue } from './race-registration/custom-field-value';
import { CompetitorViewContext } from './competitor-view-context';
import { VisibilityTypes } from './visibility-types';
import { TeamType } from './team-type';
import {TeamRaceType} from "./team-race-type";
import {
    alphabetical,
    max_length_10,
    max_length_100,
    max_length_255,
    max_length_30,
    max_length_45, max_length_5,
    max_length_50, numerical
} from "@utility";

/**
 * Ensures every key listed is a real property of `Competitor` — if a listed literal isn't actually
 * `keyof Competitor`, TS rejects it right here at the type alias below instead of the mismatch
 * surfacing later as `undefined` values when prefilling or submitting the form.
 */
type KeyOf<T, K extends keyof T> = K;

export enum CompetitorFieldGroup {
    GENERAL,
    START_INFO,
    ALL
};

/** Every editable field of the competitor form — each one is a real property on `Competitor`. */
export type CompetitorFormFieldKey = KeyOf<Competitor,
    | 'email' | 'firstName' | 'lastName' | 'sex' | 'nickname' | 'birthday'
    | 'address' | 'placeOfResidence' | 'country' | 'nationality'
    | 'identityCardNumber' | 'identityCardIssuer' | 'identityCardExpires'
    | 'mobilePhone'
    | 'emergencyPhone' | 'club' | 'bestTime'
    | 'tshirt' | 'meal' | 'transport' | 'licence' | 'discipline'
    | 'chipNumber' | 'startNumber' | 'startBlock' | 'startNumberExtension'
    >;

export type CompetitorFieldControlType = 'text' | 'select' | 'date' | 'tel' | 'number';

/**
 * Static shape of a field: which ApplicationFormSettings properties gate it, which component
 * renders it, and any per-field label/select metadata. This table is the single place a new
 * registration-form field needs to be described — the template never checks fields individually.
 */
interface CompetitorFieldDefinition {
    key: CompetitorFormFieldKey;
    controlType: CompetitorFieldControlType;
    labelKey: string;
    /** ApplicationFormSettings property that gates whether this field is used at all. */
    displayFlagProp?: keyof ApplicationFormSettings;
    /** ApplicationFormSettings property holding the "is this required" boolean, if any. */
    requiredProp?: keyof ApplicationFormSettings;
    // select-only metadata (matches the property names on the `items` passed in via itemsSource)
    itemLabelKey?: string;
    itemValueKey?: string;
    itemLabelKeyPrefix?: string;
    group?: CompetitorFieldGroup;
    /** Extra validators beyond the `required` one already derived from `requiredProp`. */
    validators?: ValidatorFn[];
}

/** Fully-resolved field ready for the template: usage/required/editable already computed. */
export interface CompetitorFieldDescriptor {
    key: CompetitorFormFieldKey;
    controlType: CompetitorFieldControlType;
    labelKey: string;
    required: boolean;
    editable: boolean;
    items?: unknown[];
    itemLabelKey?: string;
    itemValueKey?: string;
    itemLabelKeyPrefix?: string;
    minDate?: Date;
    maxDate?: Date;
    openDate?: Date;
    allowOnlyYearSelection?: boolean;
}

/** Date bounds that come from the component, not from ApplicationFormSettings. */
export interface CompetitorFormDateBounds {
    currentDate?: Date;
    minDateForBirthday?: Date;
    calendarNavigationDate?: Date;
}

/** Which contexts are allowed to edit a given field. Omit a key to leave it unrestricted (default). */
export type CompetitorFieldEditRestrictions = Partial<Record<CompetitorFormFieldKey, CompetitorViewContext[]>>;

export interface CompetitorFormHelperOptions {
    /** The single competitor this form edits — a CompetitorFormHelper is always scoped to one competitor + one race. */
    competitor?: Competitor;
    applicationFormSettings: ApplicationFormSettings | undefined;
    raceSettings?: RaceSettings;
    additionalPricingItems?: RaceAdditionalPricingItem[];
    context?: CompetitorViewContext;
    /**
     * Same meaning as in `CompetitorViewHelper`: true when this form is collecting one individual's
     * personal details (a solo competitor, or one team member's own sub-form), false when it's the
     * team-as-a-whole entry (team name only). Defaults to `!isTeamRace()` — the same rule
     * `CompetitorViewHelper.buildRaceView` uses for its main view — so you only need to pass this
     * explicitly to override that default; building a member's sub-form is `forTeamMember()` below,
     * not this option.
     */
    forSingleCompetitor?: boolean;
    /** Option lists for select fields, e.g. `{ sex: genders, country: countries, tshirt: tshirtSizes }`. */
    itemsSource?: Partial<Record<CompetitorFormFieldKey, unknown[]>>;
    dateBounds?: CompetitorFormDateBounds;
    /**
     * Per-field edit restriction, e.g. `{ bestTime: [CompetitorViewContext.ADMINISTRATION] }` to make
     * a field editable only by admins even though it's used/visible on the form. Unlisted fields are
     * editable by whoever the form is being rendered for. Note `chipNumber`'s admin-only rule is
     * already baked into `isFieldUsed` below (ported from the table helper) — this is for anything
     * additional you need.
     */
    editableInContexts?: CompetitorFieldEditRestrictions;
    formType?: CompetitorFieldGroup;
}

/**
 * Drives the registration form for exactly one competitor on exactly one race (its RaceSettings +
 * ApplicationFormSettings) — unlike `CompetitorViewHelper`, which builds view data per race across
 * a whole event, this helper never deals in a map of races.
 *
 * `fields` (or `primaryFields`/`additionalFields`) gives the template a ready-to-render, ordered
 * list of descriptors — no more `isRegistrationFieldUsed(applicationFormSettings?.xFlag)` per field.
 * `buildFormGroup()` builds the whole reactive form from that same list, applying `required` and
 * `editable` (disabled) automatically, and prefills each control straight from the `Competitor`
 * passed in — since every field key is a real `keyof Competitor`, there's no name-translation layer
 * between the form and the entity. `getFormData()` is the inverse: reads the form's current values
 * back into those same shapes, ready to send to the API.
 *
 * For the read-only table columns, use `CompetitorViewHelper` instead.
 *
 * `fieldDefinitions` (and the `displayFlagMap` derived from it) are built once per instance, in the
 * constructor, rather than as module-level `const`s. They reference several `ValidatorFn`s imported
 * from `@utility` — if that module is involved in a circular import with this file (or anything that
 * imports this file), a module-level `const FIELD_DEFINITIONS = [...]` evaluated at import time can
 * capture those validators while they're still `undefined` mid-resolution. Building the array inside
 * the constructor defers that evaluation until an instance is actually created, by which point the
 * app has fully bootstrapped and every import is guaranteed to be resolved.
 */
export class CompetitorFormHelper extends ApplicationFormFieldVisibilityResolver<CompetitorFormFieldKey> {
    public readonly competitor?: Competitor;
    public readonly applicationFormSettings: ApplicationFormSettings | undefined;
    public readonly raceSettings?: RaceSettings;
    public readonly additionalPricingItems: RaceAdditionalPricingItem[];
    public readonly forSingleCompetitor: boolean;

    private readonly itemsSource: Partial<Record<CompetitorFormFieldKey, unknown[]>>;
    private readonly dateBounds: CompetitorFormDateBounds;
    private readonly editableInContexts: CompetitorFieldEditRestrictions;
    private readonly formType: CompetitorFieldGroup;

    /** Per-instance field metadata table — see class doc comment for why this isn't module-level. */
    private readonly fieldDefinitions: CompetitorFieldDefinition[];
    private readonly displayFlagMap: Map<CompetitorFormFieldKey, keyof ApplicationFormSettings>;

    constructor(options: CompetitorFormHelperOptions) {
        super(options.context ?? CompetitorViewContext.COMPETITOR_SELF);
        this.competitor = options.competitor;
        this.applicationFormSettings = options.applicationFormSettings;
        this.raceSettings = options.raceSettings;
        this.additionalPricingItems = options.additionalPricingItems ?? [];
        this.itemsSource = options.itemsSource ?? {};
        this.dateBounds = options.dateBounds ?? {};
        this.editableInContexts = options.editableInContexts ?? {};
        this.formType = options.formType ?? CompetitorFieldGroup.GENERAL;
        this.forSingleCompetitor = options.forSingleCompetitor ?? !this.isTeamRace();

        this.fieldDefinitions = this.buildFieldDefinitions();
        this.displayFlagMap = new Map(
            this.fieldDefinitions.filter(d => d.displayFlagProp).map((d) => [d.key, d.displayFlagProp!])
        );
    }

    private buildFieldDefinitions(): CompetitorFieldDefinition[] {
        return [
            { key: 'email', group: CompetitorFieldGroup.GENERAL,  controlType: 'text', labelKey: 'app.common.email', displayFlagProp: 'emailRegistrationDisplayFlag', requiredProp: 'emailRequired', validators: [max_length_50] },
            { key: 'firstName', group: CompetitorFieldGroup.GENERAL, controlType: 'text', labelKey: 'app.common.first.name', displayFlagProp: 'nameRegistrationDisplayFlag', requiredProp: 'nameRequired', validators: [max_length_45, alphabetical] },
            { key: 'lastName', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.last.name', displayFlagProp: 'lastnameRegistrationDisplayFlag', requiredProp: 'lastnameRequired', validators: [max_length_45, alphabetical] },
            { key: 'sex', group: CompetitorFieldGroup.GENERAL, controlType: 'select',  labelKey: 'app.common.gender', displayFlagProp: 'genderRegistrationDisplayFlag', requiredProp: 'genderRequired', itemLabelKey: 'id', itemValueKey: 'id', itemLabelKeyPrefix: 'app.common.gender.' },
            { key: 'nickname', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.nickname', displayFlagProp: 'nicknameRegistrationDisplayFlag', requiredProp: 'nicknameRequired', validators: [max_length_45] },
            { key: 'birthday', group: CompetitorFieldGroup.GENERAL, controlType: 'date',  labelKey: 'app.common.birthDay', displayFlagProp: 'birthdayRegistrationDisplayFlag', requiredProp: 'birthdayRequired'},
            { key: 'address', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.address', displayFlagProp: 'addressRegistrationDisplayFlag', requiredProp: 'addressRequired', validators: [max_length_100] },
            { key: 'placeOfResidence', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.placeOfResidence', displayFlagProp: 'cityRegistrationDisplayFlag', requiredProp: 'cityRequired', validators: [max_length_45, alphabetical] },
            { key: 'country', group: CompetitorFieldGroup.GENERAL, controlType: 'select',  labelKey: 'app.common.country', displayFlagProp: 'countryRegistrationDisplayFlag', requiredProp: 'countryRequired', itemLabelKey: 'id', itemValueKey: 'id', itemLabelKeyPrefix: 'app.common.country.' },
            { key: 'nationality', group: CompetitorFieldGroup.GENERAL, controlType: 'select',  labelKey: 'app.common.nationality', displayFlagProp: 'nationalityRegistrationDisplayFlag', requiredProp: 'nationalityRequired', itemLabelKey: 'name', itemValueKey: 'id' },
            { key: 'identityCardNumber', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.identityCardNumber', displayFlagProp: 'identityCardNumberRegistrationDisplayFlag', requiredProp: 'identityCardNumberRequired', validators: [max_length_30] },
            { key: 'identityCardIssuer', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.identityCardIssuer', displayFlagProp: 'identityCardIssuerRegistrationDisplayFlag', requiredProp: 'identityCardIssuerRequired', validators: [max_length_255] },
            { key: 'identityCardExpires', group: CompetitorFieldGroup.GENERAL, controlType: 'date',  labelKey: 'app.common.identityCardExpires', displayFlagProp: 'identityCardExpiresRegistrationDisplayFlag', requiredProp: 'identityCardExpiresRequired'},
            { key: 'mobilePhone', group: CompetitorFieldGroup.GENERAL, controlType: 'tel',  labelKey: 'app.common.mobilePhone', displayFlagProp: 'mobilePhoneRegistrationDisplayFlag', requiredProp: 'mobilePhoneRequired'},
            { key: 'emergencyPhone', group: CompetitorFieldGroup.GENERAL, controlType: 'tel',  labelKey: 'app.common.emergencyPhone', displayFlagProp: 'emergencyPhoneRegistrationDisplayFlag', requiredProp: 'emergencyPhoneRequired'},
            { key: 'club', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.club', displayFlagProp: 'clubRegistrationDisplayFlag', requiredProp: 'clubRequired', validators: [max_length_100] },
            { key: 'bestTime', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.bestTime', displayFlagProp: 'bestTimeRegistrationDisplayFlag', requiredProp: 'bestTimeRequired'},
            { key: 'tshirt', group: CompetitorFieldGroup.GENERAL, controlType: 'select',  labelKey: 'app.common.tshirtSize', displayFlagProp: 'tshirtRegistrationDisplayFlag', requiredProp: 'tshirtRequired', itemLabelKey: 'name', itemValueKey: 'alternativeId' },
            { key: 'meal', group: CompetitorFieldGroup.GENERAL, controlType: 'select',  labelKey: 'app.common.meal', displayFlagProp: 'mealRegistrationDisplayFlag', requiredProp: 'mealRequired', itemLabelKey: 'alternativeId', itemValueKey: 'alternativeId', itemLabelKeyPrefix: 'app.common.meal.' },
            { key: 'transport', group: CompetitorFieldGroup.GENERAL, controlType: 'select',  labelKey: 'app.common.transport', displayFlagProp: 'transportRegistrationDisplayFlag', requiredProp: 'transportRequired', itemLabelKey: 'id', itemValueKey: 'alternativeId', itemLabelKeyPrefix: 'app.common.transport.' },
            { key: 'licence', group: CompetitorFieldGroup.GENERAL, controlType: 'text',  labelKey: 'app.common.licence', displayFlagProp: 'licenceRegistrationDisplayFlag', requiredProp: 'licenceRequired', validators: [max_length_100] },
            { key: 'discipline', group: CompetitorFieldGroup.GENERAL, controlType: 'select',  labelKey: 'app.common.discipline', displayFlagProp: 'disciplineRegistrationDisplayFlag', requiredProp: 'disciplineRequired', itemLabelKey: 'name', itemValueKey: 'id' },
            { key: 'chipNumber', group: CompetitorFieldGroup.START_INFO, controlType: 'text',  labelKey: 'app.common.chipNumber', displayFlagProp: 'chipNumberRegistrationDisplayFlag', validators: [max_length_10] },
            { key: 'startNumber', group: CompetitorFieldGroup.START_INFO, controlType: 'number',  labelKey: 'competitor.table.startNumber', validators: [max_length_10] },
            { key: 'startBlock', group: CompetitorFieldGroup.START_INFO, controlType: 'number',  labelKey: 'competitor.table.startBlock', validators: [numerical]},
            { key: 'startNumberExtension', group: CompetitorFieldGroup.START_INFO, controlType: 'text',  labelKey: 'competitor.table.startNumberExtension', validators: [max_length_5, alphabetical] },
        ];
    }

    /** Same derivation as `CompetitorViewHelper`: any team type other than SINGLE counts as a team race. */
    isTeamRace(): boolean {
        return !!this.raceSettings?.teamType && this.raceSettings.teamType !== TeamType.SINGLE;
    }

    /**
     * Builds the sibling helper for one individual team member's own sub-form — the form-side
     * counterpart of `CompetitorViewHelper.buildRaceView`'s `teamMemberView`, which is always
     * `forSingleCompetitor: true` no matter what the main view/form is. Returns `undefined` under
     * the exact same condition the view helper uses to skip building `teamMemberView`: a confirmed
     * single-type race with real race settings (`!isTeamRace() && raceSettings` present) has no
     * separate "member" concept at all, since the main form already covers the individual directly.
     */
    forTeamMember(memberCompetitor: Competitor): CompetitorFormHelper | undefined {
        const hasMemberForm = this.isTeamRace() || !this.raceSettings;
        if (!hasMemberForm) return undefined;

        return new CompetitorFormHelper({
            competitor: memberCompetitor,
            applicationFormSettings: this.applicationFormSettings,
            raceSettings: this.raceSettings,
            additionalPricingItems: this.additionalPricingItems,
            context: this.context,
            forSingleCompetitor: true,
            itemsSource: this.itemsSource,
            dateBounds: this.dateBounds,
            editableInContexts: this.editableInContexts,
            formType: this.formType,
        });
    }

    protected get visibilityFieldMap(): Map<CompetitorFormFieldKey, keyof ApplicationFormSettings> {
        // The registration form only needs "is this field used at all", not the finer-grained
        // public/admin visibility split the table needs — so there's nothing to map here.
        return new Map();
    }

    protected get displayFlagFieldMap(): Map<CompetitorFormFieldKey, keyof ApplicationFormSettings> {
        return this.displayFlagMap;
    }

    /**
     * Whether `field` should be rendered on the registration form at all — this mirrors
     * `CompetitorViewHelper.getCompetitorTableAvailableColumnsBasedOnApplicationFormSettings`,
     * which is the one place your codebase already encodes the role/team rules for these same
     * fields. Ported rules:
     *  - personal fields (sex, nickname, birthday, address, placeOfResidence, country, nationality,
     *    identityCardNumber/Issuer/Expires, emergencyPhone, mobilePhone, club, bestTime, tshirt,
     *    meal, transport, licence, comment) only appear when `forSingleCompetitor` is true.
     *  - `birthday` is additionally restricted to admin or self contexts (not public/league-organiser).
     *  - `discipline` additionally requires a team race.
     *  - `chipNumber` is admin-only, regardless of `forSingleCompetitor` (matches the table exactly).
     *  - `email`, `firstName`, `lastName` are flag-only, same as the table treats them differently
     *    from the rest (the table's own email rule is a *report visibility* rule, not a "should this
     *    be collected" rule, so it isn't ported here).
     */
    override isFieldUsed(field: CompetitorFormFieldKey): boolean {
        const usedByFlag = (f: CompetitorFormFieldKey) => super.isFieldUsed(f, this.applicationFormSettings);
        const show = (f: CompetitorFormFieldKey) => this.forSingleCompetitor && usedByFlag(f);

        // Team races need two different forms out of this same helper: the team-as-a-whole entry
        // (forSingleCompetitor = false) only collects a team name — stored in `lastName`, the same
        // convention CompetitorViewHelper already uses for the table's teamName column, where
        // `row.teamName = isTeamRace && isParentCompetitor ? lastName : undefined` and `fullName`
        // is left unset — while each member's own sub-form (forSingleCompetitor = true) collects
        // everything else.
        if (!this.forSingleCompetitor) {
            if (field === 'lastName') return usedByFlag('lastName');
            if (field === 'startNumber') return this.isAdmin();
            if (field === 'startBlock') return this.raceSettings?.blockStart ?? false;
            return false;
        }
        switch (field) {
            case 'email':
            case 'firstName':
            case 'lastName':
                return usedByFlag(field);
            case 'birthday':
                return (this.isAdmin() || this.isSelf()) && show('birthday');
            case 'discipline':
                return this.isTeamRace() && usedByFlag('discipline');
            case 'chipNumber':
                return this.isAdmin() && usedByFlag('chipNumber');
            case 'startNumber':
                return this.isAdmin() && !this.isTeamRace();
            case 'startNumberExtension':
                return this.isAdmin() && this.isTeamRace() && this.raceSettings?.teamRaceType === TeamRaceType.CONCURRENT;
            case 'startBlock':
                return this.isAdmin() && !this.isTeamRace() && (this.raceSettings?.blockStart ?? false);
            default:
                return show(field);
        }
    }

    /** Whether `field` is required, straight from its ApplicationFormSettings `*Required` flag. */
    isFieldRequired(field: CompetitorFormFieldKey): boolean {
        const def = this.fieldDefinitions.find((d) => d.key === field);
        if (!def?.requiredProp || !this.applicationFormSettings) return false;
        return !!this.applicationFormSettings[def.requiredProp];
    }

    /** Whether the current context (see `editableInContexts`) is allowed to edit `field`. */
    isFieldEditable(field: CompetitorFormFieldKey): boolean {
        const allowedContexts = this.editableInContexts[field];
        return !allowedContexts || allowedContexts.includes(this.context);
    }

    get visibleCustomFields(): FieldConfig[] {
        if (!this.forSingleCompetitor || this.formType === CompetitorFieldGroup.START_INFO)  return [];
        return (this.applicationFormSettings?.customFields ?? []).filter((f) => this.isCustomFieldVisible(f));
    }

    get visibleAdditionalPricingItems(): RaceAdditionalPricingItem[] {
        if (!this.forSingleCompetitor || this.formType === CompetitorFieldGroup.START_INFO) return [];
        return this.additionalPricingItems.filter((item) => item.fieldVisibility !== VisibilityTypes.NOT_VISIBLE);
    }

    /** All used fields, in definition order, fully resolved for the template — minus anything in `ignoredFields`. */
    get fields(): CompetitorFieldDescriptor[] {
        return this.fieldDefinitions
            .filter((def) => this.formType === CompetitorFieldGroup.ALL || def.group === this.formType)
            .filter((def) => this.isFieldUsed(def.key))
            .filter((def) => def.controlType !== 'select' || (this.itemsSource[def.key]?.length ?? 0) > 0)
            .map((def) => this.toDescriptor(def));
    }



    private toDescriptor(def: CompetitorFieldDefinition): CompetitorFieldDescriptor {
        const isTeamNameField = def.key === 'lastName' && !this.forSingleCompetitor;
        const descriptor: CompetitorFieldDescriptor = {
            key: def.key,
            controlType: def.controlType,
            labelKey: isTeamNameField ? 'app.common.team.name' : def.labelKey,
            required: this.isFieldRequired(def.key),
            editable: this.isFieldEditable(def.key),
            items: this.itemsSource[def.key],
            itemLabelKey: def.itemLabelKey,
            itemValueKey: def.itemValueKey,
            itemLabelKeyPrefix: def.itemLabelKeyPrefix,
        };

        if (def.key === 'birthday') {
            descriptor.minDate = this.dateBounds.minDateForBirthday;
            descriptor.maxDate = this.dateBounds.currentDate;
            descriptor.openDate = this.dateBounds.calendarNavigationDate;
            descriptor.allowOnlyYearSelection = this.isAdmin();
        }
        if (def.key === 'identityCardExpires') {
            descriptor.minDate = this.dateBounds.currentDate;
        }

        return descriptor;
    }

    /**
     * Builds the reactive FormGroup for this competitor: a top-level control per used field
     * (required/disabled set from the descriptor, prefilled straight from `this.competitor` since
     * every field key is a real `keyof Competitor`, plus any extra validators from `fieldDefinitions`),
     * a nested `customFieldValues` group for visible custom fields (prefilled from
     * `competitor.customFieldValues`, with their own min/max/required validators), and a
     * `raceAdditionalPricingItems` FormArray matching `visibleAdditionalPricingItems` by index,
     * prefilled from `competitor.raceAdditionalPricingItems`.
     */
    buildFormGroup(): FormGroup {
        const group = new FormGroup({});
        const existing: Partial<Competitor> = this.competitor ?? {};

        for (const field of this.fields) {
            const fieldDef = this.fieldDefinitions.find((d) => d.key === field.key);
            const validators = field.required ? [Validators.required] : [];
            if (fieldDef?.validators) validators.push(...fieldDef.validators);
            group.addControl(field.key, new FormControl({ value: existing[field.key], disabled: !field.editable }, validators));
        }

        const visibleCustom = this.visibleCustomFields;
        if (visibleCustom.length) {
            const customGroup = new FormGroup({});
            const existingCustomValuesByFieldId = new Map<number, CustomFieldValue>(
                (this.competitor?.customFieldValues ?? [])
                    .filter((v): v is CustomFieldValue & { fieldId: number } => v.fieldId != null)
                    .map((v) => [v.fieldId, v])
            );

            for (const fieldConfig of visibleCustom) {
                if (!fieldConfig.id) continue;
                const existingValue = existingCustomValuesByFieldId.get(fieldConfig.id);
                const control = new FormControl(this.toCustomFieldControlValue(fieldConfig, existingValue));
                if (fieldConfig.maxValue != null) control.addValidators(Validators.max(fieldConfig.maxValue));
                if (fieldConfig.minValue != null) control.addValidators(Validators.min(fieldConfig.minValue));
                if (fieldConfig.maxTextLength) control.addValidators(Validators.maxLength(fieldConfig.maxTextLength));
                if (fieldConfig.minTextLength) control.addValidators(Validators.minLength(fieldConfig.minTextLength));
                if (fieldConfig.required && fieldConfig.fieldConfigTypeId !== FieldConfigType.BOOLEAN) {
                    control.addValidators(Validators.required);
                }
                customGroup.addControl(fieldConfig.id.toString(), control);
            }
            group.addControl('customFieldValues', customGroup);
        }

        const visiblePricing = this.visibleAdditionalPricingItems;
        if (visiblePricing.length) {
            const existingPricingById = new Map<number, RaceAdditionalPricingItem>(
                (this.competitor?.raceAdditionalPricingItems ?? [])
                    .filter((v): v is RaceAdditionalPricingItem & { id: number } => v.id != null)
                    .map((v) => [v.id, v])
            );

            const pricingArray = new FormArray(
                visiblePricing.map((item) => {
                    const existingSelection = item.id != null ? existingPricingById.get(item.id) : undefined;
                    return new FormGroup({
                        racePricingItemValue: new FormControl(existingSelection?.pricingItemValues?.at(0)),
                    });
                })
            );
            group.addControl('raceAdditionalPricingItems', pricingArray);
        }

        return group;
    }

    /**
     * Reads the current values out of a FormGroup built by `buildFormGroup()` and reassembles them
     * into the same shapes it was populated from: a `Partial<Competitor>` for the top-level fields,
     * a `CustomFieldValue[]` rebuilt from the `customFieldValues` group, and a
     * `RaceAdditionalPricingItem[]` rebuilt from the `raceAdditionalPricingItems` FormArray.
     *
     * Uses `getRawValue()` for the top-level fields (not `.value`) so disabled/non-editable controls
     * are included too — those still hold real competitor data, just not editable in this context.
     */
    getFormData(formGroup: FormGroup): Partial<Competitor> {
        const raw = formGroup.getRawValue();

        const competitor: Partial<Competitor> = {};
        for (const field of this.fields) {
            (competitor as Record<string, unknown>)[field.key] = raw[field.key];
        }

        const customFieldValues = this.getCustomFieldValues(formGroup);
        if (customFieldValues.length) {
            competitor.customFieldValues = customFieldValues;
        }

        const raceAdditionalPricingItems = this.getRaceAdditionalPricingItems(formGroup);
        if (raceAdditionalPricingItems.length) {
            competitor.raceAdditionalPricingItems = raceAdditionalPricingItems;
        }

        return competitor;
    }

    /** Inverse of `toCustomFieldControlValue()` / the `customFieldValues` population in `buildFormGroup()`. */
    private getCustomFieldValues(formGroup: FormGroup): CustomFieldValue[] {
        const customGroup = this.customFieldValuesFormGroup(formGroup);
        if (!customGroup) return [];

        const values: CustomFieldValue[] = [];
        for (const fieldConfig of this.visibleCustomFields) {
            if (!fieldConfig.id) continue;
            const control = customGroup.get(fieldConfig.id.toString());
            if (!control) continue;

            const value: CustomFieldValue = { fieldId: fieldConfig.id };
            switch (fieldConfig.fieldConfigTypeId) {
                case FieldConfigType.BOOLEAN:
                    value.boolValue = control.value ?? undefined;
                    break;
                case FieldConfigType.SELECT:
                    value.numberList = control.value != null ? [control.value] : undefined;
                    break;
                case FieldConfigType.MULTISELECT:
                    value.numberList = control.value ?? undefined;
                    break;
                case FieldConfigType.TEXT:
                    value.inputString = control.value ?? undefined;
                    break;
                case FieldConfigType.NUMBER:
                    value.numberValue = control.value ?? undefined;
                    break;
                case FieldConfigType.DATE:
                case FieldConfigType.TIME:
                    // Not populated yet — same gap as toCustomFieldControlValue() and
                    // CompetitorViewHelper.transformCustomFieldValues.
                    break;
                default:
                    break;
            }
            values.push(value);
        }
        return values;
    }

    /** Inverse of the `raceAdditionalPricingItems` population in `buildFormGroup()`. */
    private getRaceAdditionalPricingItems(formGroup: FormGroup): RaceAdditionalPricingItem[] {
        const pricingArray = formGroup.get('raceAdditionalPricingItems') as FormArray | null;
        if (!pricingArray) return [];

        const items: RaceAdditionalPricingItem[] = [];
        this.visibleAdditionalPricingItems.forEach((item, index) => {
            const group = pricingArray.at(index) as FormGroup | null;
            const selectedValue = group?.get('racePricingItemValue')?.value;
            if (selectedValue == null || item.id == null) return;

            items.push({ ...item, pricingItemValues: [selectedValue] });
        });

        return items;
    }

    /**
     * Maps a competitor's stored custom field value to the raw value the matching form control
     * expects, based on the field's type — the same switch `CompetitorViewHelper.transformCustomFieldValues`
     * uses to produce a *display* string, just returning the raw value here instead.
     */
    private toCustomFieldControlValue(fieldConfig: FieldConfig, existingValue: CustomFieldValue | undefined): unknown {
        if (!existingValue) return undefined;

        switch (fieldConfig.fieldConfigTypeId) {
            case FieldConfigType.BOOLEAN:
                return existingValue.boolValue ?? undefined;
            case FieldConfigType.SELECT:
                return existingValue.numberList?.at(0);
            case FieldConfigType.MULTISELECT:
                return existingValue.numberList ?? [];
            case FieldConfigType.TEXT:
                return existingValue.inputString;
            case FieldConfigType.NUMBER:
                return existingValue.numberValue;
            case FieldConfigType.DATE:
            case FieldConfigType.TIME:
                // not populated yet — CompetitorViewHelper.transformCustomFieldValues has the same
                // "todo: localize date/time" gap, so there's no confirmed raw shape to read here.
                return undefined;
            default:
                return undefined;
        }
    }

    /** Convenience accessor mirroring the template's `raceAdditionalPricingItemFormControl($index)` call. */
    raceAdditionalPricingItemFormGroup(formGroup: FormGroup, index: number): FormGroup {
        return (formGroup.get('raceAdditionalPricingItems') as FormArray).at(index) as FormGroup;
    }

    /**
     * Typed accessor for the nested `customFieldValues` group — `formGroup.controls['customFieldValues']`
     * types as `AbstractControl`, not `FormGroup`, which `app-field-config-value-input` needs.
     */
    customFieldValuesFormGroup(formGroup: FormGroup): FormGroup {
        return formGroup.get('customFieldValues') as FormGroup;
    }
}